/* KATANA cart guard - keep racket customization add-on lines tied to their racket.
   The customizer (katana-options.js) adds a racket + its add-on component products in
   one /cart/add.js call. The racket line carries property _ko_group; every add-on line
   carries _ko_parent_group pointing at that same group id.

   Problem this fixes: removing the racket left the zero-value add-on lines behind, so they could
   be bought on their own. Checkout is Razorpay Magic Checkout, whose sidecart renders its
   OWN cart UI (not the theme drawer) - so a UI-level hook is not enough.

   Strategy: work at the NETWORK layer. Any cart UI (theme drawer, /cart page, Razorpay
   sidecart) must modify the Shopify cart through the standard AJAX endpoints
   (/cart/change, /cart/update, ...). We wrap fetch + XHR: after any cart mutation we
   reconcile - remove add-on lines whose parent racket is gone, match add-on qty to the
   racket qty - and (for fetch) hand the CLEANED cart back to the caller so its own UI
   renders clean. This keeps the cart clean at all times, so whatever Razorpay reads at
   pay-time never contains an orphan. Depends only on /cart.js + /cart/update.js. */
(function () {
  if (window.__koCartSyncInit) return;
  window.__koCartSyncInit = true;

  var GROUP = '_ko_group';
  var PARENT = '_ko_parent_group';
  /* endpoints that MUTATE the cart (removal / qty / add / clear) */
  var CART_MUT = /\/cart\/(change|update|add|clear)(\.js)?(\?|#|$)/i;
  /* the subset that can ORPHAN an add-on (a line went away or shrank) */
  var CART_TRIM = /\/cart\/(change|update|clear)(\.js)?(\?|#|$)/i;

  /* capture the pristine fetch BEFORE we patch it, so our own calls never re-enter */
  var rawFetch = (window.fetch && window.fetch.bind(window)) || null;
  var busy = false;

  function money(cents) {
    try {
      if (window.theme && theme.Currency && theme.Currency.formatMoney) {
        return theme.Currency.formatMoney(cents, theme.settings && theme.settings.moneyFormat);
      }
    } catch (e) {}
    if (window.Shopify && Shopify.formatMoney) return Shopify.formatMoney(cents);
    return '&#8377;' + Math.round(cents / 100).toLocaleString('en-IN');
  }

  function doFetch(url, opts) {
    var f = rawFetch || window.fetch;
    return f(url, opts);
  }

  function getCart() {
    return doFetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (r) { return r.json(); });
  }

  /* Work out what needs changing from a cart object. Returns:
     { updates: { line_key: desiredQty, ... }, removedKeys: [key,...], adjusted: [{key,qty},...] }
     - add-on whose parent group is gone      -> desiredQty 0 (remove)
     - add-on whose qty != its parent's qty    -> match parent qty */
  function plan(cart) {
    var parentQty = {};
    (cart.items || []).forEach(function (it) {
      var props = it.properties || {};
      var g = props[GROUP];
      if (g != null && g !== '') parentQty[g] = it.quantity;
    });

    var updates = {}, removedKeys = [], adjusted = [];
    (cart.items || []).forEach(function (it) {
      var props = it.properties || {};
      var pg = props[PARENT];
      if (pg == null || pg === '') return;               /* not an add-on line */
      var want = Object.prototype.hasOwnProperty.call(parentQty, pg) ? parentQty[pg] : 0;
      if (it.quantity !== want) {
        updates[it.key] = want;
        if (want === 0) removedKeys.push(it.key);
        else adjusted.push({ key: it.key, qty: want });
      }
    });
    return { updates: updates, removedKeys: removedKeys, adjusted: adjusted };
  }

  function hasWork(updates) {
    for (var k in updates) { if (Object.prototype.hasOwnProperty.call(updates, k)) return true; }
    return false;
  }

  /* POST the removals/adjustments; resolves with the cleaned cart JSON. */
  function applyUpdates(updates) {
    return doFetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ updates: updates })
    }).then(function (r) { return r.json(); });
  }

  function escapeAttr(v) { return String(v).replace(/["\\]/g, '\\$&'); }

  /* Best-effort refresh of the THEME's own cart UI (drawer / cart page) if it is on the
     page. Razorpay's sidecart refreshes itself from the cleaned response we return. */
  function patchDom(clean, removedKeys, adjusted) {
    try {
      (removedKeys || []).forEach(function (key) {
        document.querySelectorAll('.cart__item[data-key="' + escapeAttr(key) + '"]').forEach(function (row) {
          row.parentNode && row.parentNode.removeChild(row);
        });
      });
      (adjusted || []).forEach(function (a) {
        document.querySelectorAll('input.js-qty__num[data-id="' + escapeAttr(a.key) + '"]').forEach(function (input) {
          input.value = a.qty;
        });
      });
      document.querySelectorAll('[data-subtotal]').forEach(function (el) { el.innerHTML = money(clean.total_price); });
      document.querySelectorAll('.cart-link__bubble-num').forEach(function (el) { el.innerText = clean.item_count; });
      document.querySelectorAll('.cart-link__bubble').forEach(function (b) {
        b.classList.toggle('cart-link__bubble--visible', clean.item_count > 0);
      });
    } catch (e) {}
  }

  /* Standalone reconcile (used on load, on theme cart mutations, before checkout, and as
     the XHR fallback). Resolves true if it changed the cart. */
  function reconcile() {
    if (busy) return Promise.resolve(false);
    busy = true;
    return getCart()
      .then(function (cart) {
        var p = plan(cart);
        if (!hasWork(p.updates)) return false;
        return applyUpdates(p.updates).then(function (clean) {
          patchDom(clean, p.removedKeys, p.adjusted);
          return true;
        });
      })
      .catch(function () { return false; })
      .finally(function () { busy = false; });
  }

  /* =========================================================================
     PRIMARY: network interception (UI-agnostic - covers the Razorpay sidecart)
     ========================================================================= */

  function urlOf(input) {
    if (typeof input === 'string') return input;
    if (input && typeof input.url === 'string') return input.url;   /* Request object */
    return '';
  }

  if (rawFetch) {
    window.fetch = function (input, init) {
      var url = urlOf(input);
      var res = rawFetch(input, init);
      if (!url || !CART_TRIM.test(url)) return res;                 /* only trim on removal/qty/clear */
      return res.then(function (response) {
        if (!response || !response.ok) return response;
        return response.clone().json().then(function (cart) {
          if (!cart || !cart.items) return response;               /* not a cart JSON body */
          var p = plan(cart);
          if (!hasWork(p.updates)) return response;                /* nothing orphaned */
          return applyUpdates(p.updates).then(function (clean) {
            patchDom(clean, p.removedKeys, p.adjusted);
            /* hand the CLEANED cart to the original caller so its UI renders clean */
            return new Response(JSON.stringify(clean), {
              status: 200,
              statusText: 'OK',
              headers: { 'Content-Type': 'application/json' }
            });
          }).catch(function () { return response; });
        }).catch(function () { return response; });                /* body was not JSON */
      });
    };
  }

  /* XHR fallback: some carts use XMLHttpRequest. We cannot cleanly rewrite an XHR
     response body, so after a cart-mutating XHR completes we reconcile server-side
     (removes the orphan) and refresh the theme DOM. The cart is left clean regardless
     of whether the caller re-renders immediately. */
  (function () {
    var OrigXHR = window.XMLHttpRequest;
    if (!OrigXHR) return;
    var open = OrigXHR.prototype.open;
    var send = OrigXHR.prototype.send;
    OrigXHR.prototype.open = function (method, url) {
      this.__koUrl = url || '';
      return open.apply(this, arguments);
    };
    OrigXHR.prototype.send = function () {
      var xhr = this;
      if (xhr.__koUrl && CART_TRIM.test(xhr.__koUrl)) {
        xhr.addEventListener('load', function () {
          /* let the caller finish its own onload first, then clean up */
          setTimeout(reconcile, 0);
        });
      }
      return send.apply(this, arguments);
    };
  })();

  /* =========================================================================
     SECONDARY nets (theme drawer / cart page / checkout / load / focus)
     ========================================================================= */

  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () { scheduled = false; reconcile(); }, 80);
  }

  function observe(container) {
    if (!container || container.__koObserved) return;
    container.__koObserved = true;
    new MutationObserver(schedule).observe(container, { childList: true, subtree: true });
  }

  function wireObservers() {
    observe(document.getElementById('CartDrawer'));
    observe(document.getElementById('CartDrawerForm'));
    observe(document.getElementById('CartPageForm'));
    document.querySelectorAll('[data-products]').forEach(function (list) {
      observe(list.closest('form') || list.parentNode);
    });
  }

  /* Pre-checkout net for the THEME's own checkout button (Razorpay's own pay button is
     already covered because interception keeps the cart clean before pay-time). */
  var reconciledForCheckout = false;
  function guardCheckout(e, resume) {
    if (reconciledForCheckout) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    reconcile().finally(function () { reconciledForCheckout = true; resume(); });
  }
  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[name="checkout"], .cart__checkout');
    if (!btn) return;
    guardCheckout(e, function () { btn.click(); });
  }, true);
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || (form.id !== 'CartDrawerForm' && form.id !== 'CartPageForm')) return;
    guardCheckout(e, function () {
      if (typeof form.requestSubmit === 'function') form.requestSubmit();
      else form.submit();
    });
  }, true);

  function init() { wireObservers(); reconcile(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', wireObservers);
  /* returning to the tab / restoring from bfcache: make sure nothing orphaned lingers */
  window.addEventListener('pageshow', schedule);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') schedule();
  });

  /* FINAL safety net: a light poll while the tab is visible. Catches cart changes made
     where interception cannot see them (e.g. a widget running in an iframe or another
     tab). One small /cart.js read; /cart/update.js is only called when an orphan
     actually exists, so steady-state cost is negligible. */
  setInterval(function () {
    if (document.visibilityState === 'visible') reconcile();
  }, 5000);
})();
